import React from 'react'

import {
  ItemSelector,
  type ItemSelectorProps,
} from '~/components/elements/ItemSelector'
import { type Brand as BrandBase } from '~/gql/graphql'
import { type Unassigned, unassignedSymbol } from '~/utils'

type Brand = App.PickGraphQL<BrandBase, 'id' | 'name'>

export type BrandSelectorProps<TBrand extends Brand> = Omit<
  ItemSelectorProps,
  'data' | 'dataItemKey' | 'onChange' | 'textField' | 'value'
> & {
  brands: TBrand[]
  isNullSelectable?: boolean
  nullText?: string
  onChange?: (brandId: TBrand['id']) => void
  unsetText?: string
  value: TBrand['id'] | Unassigned | null
}

export const BrandSelector = React.forwardRef(
  <TBrand extends Brand>(
    {
      brands,
      isNullSelectable = false,
      nullText,
      onChange,
      unsetText,
      value: valueProp,
      ...rest
    }: BrandSelectorProps<TBrand>,
    ref: React.ForwardedRef<HTMLSpanElement>,
  ) => {
    const data = React.useMemo(
      () =>
        isNullSelectable ? [{ id: null, name: nullText }, ...brands] : brands,
      [brands, isNullSelectable, nullText],
    )
    const value = React.useMemo(
      () =>
        data.find(({ id }) => id === valueProp) ?? {
          id: unassignedSymbol,
          name: unsetText,
        },
      [data, unsetText, valueProp],
    )

    const handleChange = React.useCallback<EH<ItemSelectorProps, 'onChange'>>(
      (brand: TBrand) => {
        onChange?.(brand.id)
      },
      [onChange],
    )

    return (
      <ItemSelector
        ref={ref}
        data={data}
        dataItemKey="id"
        textField="name"
        value={value}
        onChange={handleChange}
        {...rest}
      />
    )
  },
)
